import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { catchError, concatMap, first, of } from 'rxjs';
import { NgbModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { RecaptchaV3Module, ReCaptchaV3Service } from 'ng-recaptcha';

import { PaymentsService } from 'app/shared/services/payments.service';
import { ProfileService } from '../../../shared/services/profile.service';
import { TokensService } from '../../../shared/services/tokens.service';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { ProfileStateService } from 'app/shared/services/profile-state.service';
import { StatsigService } from 'app/shared/services/statsig.service';

import { SvgIconComponent } from '../../../shared/components/svg-icon/svg-icon.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { PaymentFormComponent } from '../payment-form/payment-form.component';
import { CodeVerifyComponent } from '../code-verify/code-verify.component';
import { PaymentSucceedComponent } from '../payment-succeed/payment-succeed.component';
import { CheckoutAdditionalInfoComponent } from './checkout-additional-info/checkout-additional-info.component';
import { RecaptchaTermsComponent } from '../../../shared/components/recaptcha-terms/recaptcha-terms.component';
import { PaymentFailedComponent } from '../payment-failed/payment-failed.component';

import { ISvgConfig } from '../../../shared/interfaces/svg.interfaces';
import { IButtonConfig } from '../../../shared/interfaces/button.interfaces';
import { IBillingInfo, IUserProfile } from '../../../interfaces/profile.interfaces';
import {
  IPaymentErrorMessage,
  IPaymentResult,
  IPurchaseData,
  ISubscriptionData
} from 'app/interfaces/payments.interfaces';
import { ITokenPlan } from 'app/shared/interfaces/token.interfaces';

import { ESvgTypes } from '../../../shared/enums/svg.enums';
import { EButtonSizes, EButtonTypes } from '../../../shared/enums/button.enums';
import {
  EPaymentErrorMessage,
  EPaymentErrorTitle,
  EPaymentStatus,
  EPaymentType
} from 'app/enums/payments.enums';
import { ETermsOfService } from 'app/enums/terms-of-service.enums';
import { ELocalStorageKey } from '../../../shared/enums/shared.enums';
import { EFeatureGates } from 'app/shared/enums/feature-gates.enums';

@Component({
  selector: 'stxt-checkout',
  standalone: true,
  imports: [
    CommonModule,
    NgbModalModule,
    ReactiveFormsModule,
    RecaptchaV3Module,
    SvgIconComponent,
    ButtonComponent,
    PaymentFormComponent,
    CodeVerifyComponent,
    CheckoutAdditionalInfoComponent,
    RecaptchaTermsComponent,
    PaymentFailedComponent
  ],
  templateUrl: './checkout.component.html',
  styleUrl: './checkout.component.scss',
  providers: [ReCaptchaV3Service]
})
export class CheckoutComponent implements OnInit {
  @Input() firstCheckOut: boolean;
  @Input() paymentType: string;
  @Input() creator: string;
  @Input() creatorId: string;
  @Input() subscriptionData?: ISubscriptionData;
  @Input() purchaseData?: IPurchaseData;
  @Input() tokenData?: ITokenPlan;
  @Input() billingData: IBillingInfo[];
  @Input() wsConnectionId: string;
  @Output() closeCheckoutEvent: EventEmitter<IPaymentResult> = new EventEmitter<IPaymentResult>();
  @Output() openPaymentList: EventEmitter<void> = new EventEmitter<void>();
  @Output() isEditingTokens: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('paymentForm') paymentForm: PaymentFormComponent;
  public editSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'pen' };
  public tokenCoinsSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'coins-solid' };
  public oldPaymentMethodId: string | null = null;
  public confirmBtn: IButtonConfig = {
    text: 'Confirm purchase',
    fill: EButtonTypes.SecondaryFilled,
    buttonSize: EButtonSizes.Default
  };
  public editMode: boolean = false;
  public termsForm: FormGroup;
  public user = JSON.parse(localStorage.getItem('user'));
  public userId = localStorage.getItem(ELocalStorageKey.UserId);
  public userProfile: IUserProfile;
  public verificationState = {
    isVerified: false,
    isVerifying: false
  };
  public readonly EPaymentType = EPaymentType;
  public readonly ETermsOfService = ETermsOfService;
  public isPaymentFormValid: boolean;
  public showPaymentProcessing: boolean = false;
  public paymentErrorMessage: IPaymentErrorMessage;
  public isPaymentError: boolean = false;
  public isRecaptchaEnabled: boolean;

  constructor(
    public modal: NgbModal,
    public formBuilder: FormBuilder,
    public paymentsService: PaymentsService,
    public profileService: ProfileService,
    public recaptchaV3Service: ReCaptchaV3Service,
    private readonly tokensService: TokensService,
    private readonly analyticsService: AnalyticsService,
    private readonly profileStateService: ProfileStateService,
    private readonly statsigService: StatsigService
  ) {}

  ngOnInit(): void {
    this.getCurrentUser();
    this.initTermsForm();
    if (this.subscriptionData === undefined) {
      this.subscriptionData = { price: 500, code: '' };
    }
    this.isPaymentFormValid = !!this.billingData;
    this.trackPaymentEvent();
    this.checkStatsigFeatureGate(EFeatureGates.RECAPTCHA);
  }

  getCurrentUser(): void {
    this.profileService
      .getProfileData()
      .pipe(first())
      .subscribe(profile => {
        this.userProfile = profile;
        if (!profile.email_verified) {
          this.verificationState.isVerifying = true;
        }
        this.verificationState.isVerified = profile.email_verified;
      });
  }

  initTermsForm(): void {
    this.termsForm = this.formBuilder.group({
      acceptTerms: [false, Validators.requiredTrue]
    });
  }

  setUserVerified(): void {
    this.verificationState.isVerified = true;
  }

  editBillingMethod(id: string): void {
    this.oldPaymentMethodId = id;
    this.editMode = true;
  }

  onFormValidityChange(isError: boolean): void {
    this.isPaymentFormValid = !isError;
  }

  confirmPayment(): void {
    if (this.isRecaptchaEnabled) {
      this.recaptchaV3Service
        .execute('importantAction')
        .pipe(first())
        .subscribe({
          next: () => {
            this.processPaymentConfirmation();
          }
        });
    } else {
      this.processPaymentConfirmation();
    }
  }

  processPaymentConfirmation(): void {
    if (this.termsForm.invalid) return;

    if (this.editMode || !this.billingData?.length) {
      this.paymentForm.checkErrors();
    }

    if (!this.isPaymentFormValid) return;
    this.confirmBtn.isLoading = true;
    this.showPaymentProcessing = this.isPaymentFormValid;
    this.handlePaymentConfirmCheck();
  }

  handlePaymentConfirmCheck(): void {
    if (this.billingData && this.billingData.length > 0 && !this.editMode) {
      switch (this.paymentType) {
        case EPaymentType.Subscription:
          this.subscriptionPayment(this.billingData[0].id);
          break;
        case EPaymentType.Token:
          this.tokenPurchase(this.billingData[0].id);
          break;
      }
    } else {
      this.paymentsService.$submitPayment.next(true);
    }
  }

  tokenPurchase(id: string): void {
    this.showPaymentProcessing = true;
    this.tokensService
      .purchaseTokens({
        billing_token: id,
        tokens_pack_id: this.tokenData.id,
        quantity: 1
      })
      .pipe(
        first(),
        concatMap(() => {
          return this.tokensService.fetchTokensBalance(true).pipe(
            first(),
            catchError(() => {
              this.initResultEvent(EPaymentStatus.Error, EPaymentType.Token);
              this.confirmBtn.isLoading = false;
              return of(null);
            })
          );
        })
      )
      .subscribe({
        next: () => {
          this.initResultEvent(EPaymentStatus.Success, EPaymentType.Token);
          this.tokensService.successTokenMediaPurchaseEvent$.next(true);
          this.confirmBtn.isLoading = false;
        },
        error: error => {
          this.checkPaymentResult({
            status: EPaymentStatus.Error,
            paymentType: EPaymentType.Token,
            error_message: error.error.error_message
          });
          this.confirmBtn.isLoading = false;
          setTimeout(() => (this.isPaymentError = false), 5000);
        }
      });
  }

  subscriptionPayment(id: string): void {
    this.paymentsService
      .creatorSubscriptionRequest({
        creator_id: this.creatorId,
        billing_token: id,
        plan_code: this.subscriptionData.code,
        ws_conn_id: this.wsConnectionId
      })
      .pipe(first())
      .subscribe({
        next: () => {
          this.initResultEvent(EPaymentStatus.Success, EPaymentType.Subscription);
          this.confirmBtn.isLoading = false;
        },
        error: err => {
          if (err.status === 200) {
            this.initResultEvent(EPaymentStatus.Success, EPaymentType.Subscription);
          } else {
            this.checkPaymentResult({
              status: EPaymentStatus.Error,
              paymentType: EPaymentType.Subscription,
              error_message: err.error.error_message
            });
          }
          this.confirmBtn.isLoading = false;
        }
      });
  }

  checkPaymentResult(paymentResult: IPaymentResult): void {
    if (paymentResult.status === EPaymentStatus.Success) {
      this.handlePaymentSuccess(paymentResult);
    } else {
      this.handlePaymentError(paymentResult);
    }
  }

  handlePaymentSuccess(paymentResult: IPaymentResult): void {
    this.initResultEvent(EPaymentStatus.Success, paymentResult.paymentType);
    if (this.paymentType === EPaymentType.Token) return;
    const modalRef = this.modal.open(PaymentSucceedComponent);
    modalRef.componentInstance.paymentType = paymentResult.paymentType;
  }

  handlePaymentError(paymentResult: IPaymentResult): void {
    this.isPaymentError = true;
    this.paymentErrorMessage = {
      errorTitle: EPaymentErrorTitle.generic,
      errorMessage: paymentResult.error_message || EPaymentErrorMessage.generic
    };
    this.confirmBtn.isLoading = false;
  }

  async checkStatsigFeatureGate(featureGate: EFeatureGates): Promise<void> {
    if (!this.userId) return;
    try {
      await this.statsigService.waitForInitialization();
      this.isRecaptchaEnabled = await this.statsigService.checkFeatureGate(featureGate);
    } catch (error) {
      this.isRecaptchaEnabled = false;
    }
  }

  trackPaymentEvent(): void {
    this.analyticsService.paymentInitiated(this.userId, this.user.username);
  }

  initResultEvent(status: EPaymentStatus, paymentType: EPaymentType | string): void {
    this.closeCheckoutEvent.emit({
      status: status,
      paymentType: paymentType
    });
    /*
    Refresh profile data after payment to ensure it reflects any changes,
    such as a new card or payment method, or updates to verification status.
    */
    this.profileStateService.refreshBillingInfo();
    this.profileStateService.refreshProfileData();
    this.profileStateService.refreshTransactions();
    this.profileStateService.refreshUserSubscriptions();
  }
}
